import { isGlMapHideOperation } from '@festina/configuration-general-types/configuration'
import { Stack } from '@festina-life/ui/structure'

import { configurationItem } from '../../system/paths'
import { useNavigationParams } from '../../system/useNavigationParams'
import {
  getGlMapOperationFromDate,
  getGlMapOperationMappingId,
} from '../../uiModels/glMapOperations/helpers'
import type { IndexedGlMapOperationWithMetaData } from '../../uiModels/glMapOperations/types'
import { fromDateAsLocalDate } from '../../util/versionDateUtils'
import { useOperationsContext } from '../../workspace/operationsContext'
import { OperationVersionTooltipEntry } from '../versionedTimeline/OperationVersionTooltipEntry'
import { useIsGlMapVersionActive } from './useIsGlMapVersionActive'

type Props = {
  versions: IndexedGlMapOperationWithMetaData[]
  earliestVersionDate: Date
}

/**
 * Describes the GL-map versions sharing a date on the timeline. GL-map versions are
 * selected by changing the view date rather than by navigation, so no link is offered.
 */
export const GlMapVersionsTooltipContent = ({
  versions,
  earliestVersionDate,
}: Props) => {
  const isVersionActive = useIsGlMapVersionActive()
  const { workspaceKey, branchKey } = useOperationsContext()
  const { server } = useNavigationParams()

  return (
    <Stack className="flex-col">
      {versions.map((version) => {
        const fromDate = getGlMapOperationFromDate(version.internalOperation)
        const fromDateAsDate = fromDateAsLocalDate(fromDate)

        const identifier =
          getGlMapOperationMappingId(version.internalOperation) ?? '-'

        return (
          <OperationVersionTooltipEntry
            key={version.internalOperation.operationKey}
            identifier={identifier}
            isActive={isVersionActive(version)}
            isDraft={version.isDraft}
            isInherited={false}
            isHideVersion={isGlMapHideOperation(version.internalOperation)}
            fromDate={fromDate}
            changesetHistory={version.changesetHistory}
            layer={version.layer}
            block={version.block}
            to={{
              path: configurationItem({
                block: version.block,
                layer: version.layer,
                itemId: identifier,
                type: 'glMap',
                workspaceName: workspaceKey?.name,
                branch: branchKey?.name,
                server: workspaceKey?.branchKey.server ?? server,
                untilChangesetKey: version.changesetHistory?.changesetKey,
              }),
              atTime: fromDateAsDate ?? earliestVersionDate,
            }}
          />
        )
      })}
    </Stack>
  )
}
